import type { FilterSelection } from '@/domain/prices/filters/types'
import type { Price } from '@/domain/prices/prices/types'
import type { RoomInfo } from '@/domain/prices/rooms/types'
import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import type { PriceTableAnalytics } from '@/scopes/prices/domain/types'
import type { RouteInfo } from '../transports/types'
import { FlightType } from '@/domain/prices/transports/types'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'

export interface PricesFilterClickEventProps {
  element: 'dropdown' | 'participants selector' | 'tab'
  type: string
  result: string | number | readonly string[] | undefined
}

export function createPricesFilterClickEvent({
  element,
  type,
  result,
}: PricesFilterClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'clickAccoTabsFilter',
    description: 'set filter on price and book',
    element,
    type,
    result: result?.toString().toLowerCase(),
    interaction: true,
  }
}

export interface PricesViewedEventProps {
  accoId: string
  accoBrand: string
  transport: string
  boardType: string
  durations: string
  partyComposition: string
  collection: string
  roomType: string
  departureDate: string
  totalGuests: string
  rating: string
  accoStars: string
  accoName: string
  accoCategory: string
  cityId: string
  regionId: string
  entryPrice: number
  countryId: string
  originList: string
}

export interface ViewPricesEvent {
  event: string
  ecommerce: Ecommerce
  accoId: string
  accoName: string
  accoBrand: string
  accoCategory: string
  accoStars: string
  transport: string
  boardType: string
  duration: string
  rating: string
  partyComposition: string
  totalGuests: string
  cityId: string
  regionId: string
  countryId: string
  entryPrice: number
  promotedPrice: string
  departureDate: string
  originalDate: string
}

interface Ecommerce {
  add: {
    actionField: {
      list: string
    }
    products: Record<string, string>[]
  }
}

export function createPricesViewedEvent({
  accoId,
  accoBrand,
  transport,
  boardType,
  durations,
  partyComposition,
  totalGuests,
  rating,
  accoStars,
  accoName,
  accoCategory,
  cityId,
  regionId,
  countryId,
  originList,
  entryPrice,
  departureDate,
}: PricesViewedEventProps): ViewPricesEvent {
  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate)
  return {
    event: 'viewPrices',
    ecommerce: {
      add: {
        actionField: {
          list: originList,
        },
        products: [
          {
            id: accoId,
            name: accoName,
            brand: accoBrand,
            category: accoCategory,
            quantity: '0',
            dimension2: accoStars,
            dimension3: countryId,
            dimension4: transport,
            dimension5: partyComposition,
            dimension6: '', // badges
            dimension7: rating,
            dimension8: durations,
            dimension9: partyComposition,
            dimension10: `${daysBeforeDeparture}`, // days before departure
            dimension11: totalGuests,
            dimension28: '', // roomtype
            dimension38: boardType,
            cityId,
            regionId,
            countryId,
          },
        ],
      },
    },
    accoId,
    accoBrand,
    transport,
    boardType,
    duration: durations,
    partyComposition,
    totalGuests,
    rating,
    accoStars,
    accoName,
    accoCategory,
    cityId,
    regionId,
    countryId,
    entryPrice,
    promotedPrice: 'shown',
    departureDate: '',
    originalDate: '',
  }
}

interface ViewPricesClickEventProps {
  price: number
  isHighlighted: boolean
}

export function createPricesViewedClickEvent({ price, isHighlighted }: ViewPricesClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'viewPrices',
    description: 'click on price in price table',
    element: 'price cell',
    type: isHighlighted ? 'High price' : 'Low price',
    result: price,
    interaction: true,
  }
}

export interface PricesAdditionalDetailsEventProps {
  accoId: string
  transport: string
  boardType: string
  duration: string
  partyComposition: string
  totalGuests: string
  departureDate: string
  entryPrice: string
  campaignId: string
  cityId: string
  regionId: string
  countryId: string
}

export interface AdditionalDetailsEvent {
  event: string
  accoId: string
  transport: string
  boardType: string
  duration: string
  partyComposition: string
  totalGuests: string
  departureDate: string
  entryPrice: string
  campaignId: string
  promotedPrice: string
  cityId: string
  regionId: string
  countryId: string
}

export function createAdditionalDetailsEvent({
  accoId,
  transport,
  boardType,
  duration,
  partyComposition,
  totalGuests,
  departureDate,
  entryPrice,
  campaignId,
  cityId,
  regionId,
  countryId,
}: PricesAdditionalDetailsEventProps): AdditionalDetailsEvent {
  return {
    event: 'eec.additionalDetail',
    accoId,
    transport,
    boardType,
    duration,
    partyComposition,
    totalGuests,
    departureDate,
    entryPrice,
    campaignId,
    promotedPrice: 'shown',
    cityId,
    regionId,
    countryId,
  }
}

export interface FlightEvent {
  event: string
  flightProvider: string
  arrivalAirport: string
  departureAirport: string
  departureDate: string
  departureTime: string
  price: string
  isDefaultOption: string
  type: 'flex' | 'non-flex' | null
}

export function createFlightSelectedEvent({ route, type }: { route: RouteInfo, type: FlightType }): FlightEvent {
  const flightType = route.flightType === 'flexible' ? 'flex' : 'non-flex'
  return {
    event: type === FlightType.Outbound ? 'outboundFlight' : 'inboundFlight',
    flightProvider: route.airline?.id ?? 'unknown',
    arrivalAirport: route.route?.to?.code,
    departureAirport: route.route?.from?.code,
    departureDate: route.departureDate?.fullDate,
    departureTime: route.departureDate?.time,
    price: route.price?.toString(),
    isDefaultOption: route.isDefault ? 'yes' : 'no',
    type: flightType,
  }
}

export function createClickedFlightEvent(type: FlightType): GA4AnalyticsEvent {
  return {
    event: 'Flight',
    description: 'Flight component',
    element: 'element_clicked',
    type: type === FlightType.Outbound ? 'outbound' : 'inbound',
    result: 'Select flight',
    interaction: false,
  }
}

interface InteractionEventProps {
  type: string
  result: string
}

export function createFlightComponentInteractionEvent({ type, result }: InteractionEventProps): GA4AnalyticsEvent {
  return {
    event: 'Flight',
    description: 'Flight component',
    element: 'button_group',
    type,
    result,
    interaction: true,
  }
}

export function createRoomSelectorInteractionEvent({ type, result }: InteractionEventProps): GA4AnalyticsEvent {
  return {
    event: 'PDP',
    description: 'roomtype selector',
    element: 'Accordion',
    type,
    result,
    interaction: true,
  }
}

export interface ParticipantsAllocatedEventProps {
  accoName: string
  accoBrand: string
  accoId: string
  breadcrumbs: string
  accoStars: string
  cityId: string
  rating: string
  transportType: string
  boardType: string
  duration: string
  departureDate: string
  partyComposition: string
  totalGuests: string
  regionId: string
  countryId: string
  price: number
  roomType: string
  campaignId: string
}

function getDaysBeforeDeparture(departureDate: string) {
  // TODO Caution! Date.now() is a test enemy, should be replaced as a dependency or provide this value precalculated
  return Math.floor((new Date(departureDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

export function createParticipantsAllocatedEvent(props: ParticipantsAllocatedEventProps) {
  const daysBeforeDeparture = getDaysBeforeDeparture(props.departureDate)
  return {
    event: 'eec.add',
    ecommerce: {
      add: {
        actionField: {
          list: 'participants allocated',
        },
        products: [
          {
            name: props.accoName,
            id: props.accoId,
            brand: props.accoBrand,
            category: props.breadcrumbs,
            quantity: 1,
            accoStars: props.accoStars,
            locationId: props.cityId,
            rating: props.rating,
            transport: props.transportType,
            boardType: props.boardType,
            duration: props.duration,
            departureDate: props.departureDate,
            totalGuests: props.totalGuests,
            partyComposition: props.partyComposition,
            daysBeforeDeparture,
            price: Number(props.price.toFixed(2)),
            roomType: props.roomType,
            priceHighlighted: '',
            campaignId: props.campaignId,
            cityId: props.cityId,
            regionId: props.regionId,
            countryId: props.countryId,
            entryPrice: props.price,
          },
        ],
      },
    },
  }
}

export interface AlertServiceAnalyticsEvent extends GA4AnalyticsEvent {
  eventValue: number
}

export interface SubmitEventProps {
  selectedRooms: RoomInfo[]
  analytics: PriceTableAnalytics
  filterSelection: FilterSelection
  selectedPrice?: Price
}

export function createSubmitEvent({ selectedRooms, analytics, filterSelection, selectedPrice }: SubmitEventProps) {
  const {
    accoId: id,
    accoName: name,
    accoBrand: brand,
    breadcrumbs: category,
    stars,
    rating: accoRating,
    cityId: accoCityId,
    regionId: accoRegionId,
    countryId: accoCountryId,
    originList,
  } = analytics
  const { TransportType: transportType, Participants } = filterSelection
  const {
    mealplan: boardType = '',
    duration = '',
    departureDate = '',
    minPricePerPerson: price = 0,
  } = selectedPrice || {}
  const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
    Participants || [],
  )
  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate)
  const roomType = selectedRooms.map(({ name: roomName }) => roomName).join(' - ')

  return {
    event: 'eec.submit',
    ecommerce: {
      add: {
        actionField: {
          list: originList,
        },
        products: [
          {
            id,
            name,
            brand,
            category,
            quantity: ' 1',
            price,
            dimension2: stars,
            dimension3: accoCountryId,
            dimension4: transportType,
            dimension7: accoRating,
            dimension5: partyComposition,
            dimension8: duration.toString(),
            dimension9: partyComposition,
            dimension10: daysBeforeDeparture,
            dimension11: totalGuests,
            dimension13: departureDate,
            dimension28: roomType,
            dimension38: boardType,
            dimension47: departureDate,
            cityId: accoCityId,
            regionId: accoRegionId,
            countryId: accoCountryId,
          },
        ],
      },
    },
    accoId: id,
    accoName: name,
    accoBrand: brand,
    accoCategory: category,
    accoStars: stars,
    transport: transportType,
    boardType,
    duration: duration.toString(),
    rating: accoRating,
    partyComposition,
    totalGuests,
    departureDate,
    originalDate: departureDate,
    entryPrice: price,
    promotedPrice: 'shown',
    cityId: accoCityId,
    regionId: accoRegionId,
    countryId: accoCountryId,
  }
}
